<template>
  <aside v-if="portefeuilles" class="menu">
    <b-modal :active.sync="showModal" has-modal-card>
      <CGAModal :documentUrl="pdfUrl" :portefeuille-id="cgaPortefeuilleId" :show-modal="showModal" @close-modal="showModal = false" v-show="showModal" />
    </b-modal>
    <div v-for="portefeuille in portefeuilles" :key="'portefeuille' + portefeuille.id">
      <div class="menu-head">
        <a class="has-toggle" @click="handleToggleClick(portefeuille.id)">
          <div>
            <b-icon v-if="cgaPending === portefeuille.id" icon="spinner" custom-class="fa-spin" />
            <b-icon v-else :icon="expandedPortefeuille === portefeuille.id
                ? 'minus-square'
                : 'plus-square'
              " />
          </div>
        </a>
        <router-link :to="`/portefeuille/${portefeuille.id}/`" @click.native="getCGA(portefeuille.id)">
          {{ portefeuille.name }}
        </router-link>
      </div>
      <ul v-show="expandedPortefeuille === portefeuille.id" class="menu-list">
        <li v-for="site in sites" :key="'site' + site.id">
          <a class="has-toggle" @click="toggleExpand(site.id)">
            <div>
              <b-icon :icon="isExpanded(site.id) ? 'minus-square' : 'plus-square'" />
            </div>
            {{ site.name }}
          </a>
          <ul v-show="isExpanded(site.id)">
            <li v-for="bien in site.biens" :key="'bien' + bien.id">
              <router-link :to="`/portefeuille/${portefeuille.id}/${site.id}/${bien.id}`">
                {{ bien.name }}
              </router-link>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </aside>
</template>

<script>
import { mapState } from 'vuex'
import CGAModal from './CGAModal.vue'

export default {
  components: {
    CGAModal,
  },
  data () {
    return {
      expanded: [],
      expandedPortefeuille: null,
      showModal: false,
      pdfUrl: null,
      // Portefeuille dont les conditions sont soumises a acceptation, transmis a la fenetre
      // plutot que deduit de l'URL.
      cgaPortefeuilleId: null,
      // Portefeuille dont le controle des conditions generales est en cours. Le controle est
      // un aller-retour jusqu'a M-Files : ce temoin porte l'attente sur le « + ».
      cgaPending: null,
    }
  },
  computed: {
    ...mapState({
      biens: (state) => state.inv.biens,
    }),
    ...mapState(['user']),
    sites () {
      return this.biens
        .filter((i) => i.portefeuille === this.expandedPortefeuille)
        .reduce((sites, bien) => {
          let siteIndex = sites.findIndex((i) => i.id === bien.site)
          if (siteIndex === -1) {
            siteIndex = sites.length
            sites.push({
              id: bien.site,
              name: bien.siteName,
              biens: [],
            })
          }
          sites[siteIndex].biens.push({
            id: bien.id,
            name: bien.name,
          })
          return sites
        }, [])
    },
    portefeuilles () {
      return this.biens.reduce((portefeuilles, bien) => {
        let portefeuilleIndex = portefeuilles.findIndex(
          (i) => i.id === bien.portefeuille
        )
        if (portefeuilleIndex === -1) {
          portefeuilleIndex = portefeuilles.length
          portefeuilles.push({
            id: bien.portefeuille,
            name: bien.portefeuilleName,
          })
        }
        return portefeuilles
      }, [])
    },
  },
  created () {
    // `.catch()` plutot qu'un `created` asynchrone : attendre ici retarderait la mise en
    // place du sondage et le depliage issu de la route, qui suivent immediatement.
    this.$store.dispatch('inv/getBiens').catch((e) => {
      console.error('getBiens:', e)
      this.apiError(e)
    })

    this._pollInterval = setInterval(() => {
      // Sondage sans toast : une panne prolongee du coffre en declencherait un toutes les
      // soixante secondes, alors que le premier appel a deja signale le probleme.
      this.$store.dispatch('inv/getBiens', true).catch((e) => {
        console.error('getBiens (sondage):', e)
      })
    }, 60000)

    if (this.$route.params.portefeuille) {
      this.expandedPortefeuille = +this.$route.params.portefeuille
    }
    if (this.$route.params.site) {
      this.toggleExpand(+this.$route.params.site)
    }
  },
  beforeDestroy () {
    clearInterval(this._pollInterval)
  },
  methods: {
    async handleToggleClick (portefeuilleId) {
      let cga

      // La remise a zero est dans le `finally` : le temoin doit s'effacer aussi lorsque le
      // controle echoue.
      this.cgaPending = portefeuilleId
      try {
        cga = await this.$store.dispatch('getCgaValue', {
          user: this.user.name,
          id: portefeuilleId,
        })
      } catch (e) {
        this.apiError(e)
        return
      } finally {
        this.cgaPending = null
      }

      const autorise = cga.isMandatory === false ||
        (cga.isMandatory === true && cga.isUserAccepted === true)

      if (autorise) {
        this.expandedPortefeuille = this.expandedPortefeuille === portefeuilleId ? null : portefeuilleId
        return
      }

      // Conditions exigees et non acceptees : le « + » propose l'acceptation au lieu de
      // deplier.
      await this.openCgaModal(portefeuilleId)
    },
    isExpanded (id) {
      return this.expanded.indexOf(id) !== -1
    },
    toggleExpand (id) {
      const index = this.expanded.indexOf(id)
      if (index === -1) {
        this.expanded.push(id)
      } else {
        this.expanded.splice(index, 1)
      }
    },
    // Clic sur le libelle du portefeuille : la navigation est geree par le
    // router-link, on ne fait ici que proposer l'acceptation si elle manque.
    async getCGA (id) {
      let cga
      try {
        cga = await this.$store.dispatch('getCgaValue', {
          user: this.user.name,
          id: id,
        })
      } catch (e) {
        this.apiError(e)
        return
      }

      if (cga.isMandatory === true && cga.isUserAccepted === false) {
        await this.openCgaModal(id)
      }
    },

    /**
     * Ouvre la fenetre d'acceptation et y charge le document.
     *
     * La fenetre s'ouvre avant le telechargement et affiche son propre message
     * d'attente : sinon le clic reste sans effet visible pendant tout le
     * rapatriement du PDF.
     */
    async openCgaModal (id) {
      this.pdfUrl = null
      this.cgaPortefeuilleId = id
      this.showModal = true

      try {
        // Le contenu du PDF n'est demande qu'ici : c'est le seul endroit qui
        // l'utilise.
        this.pdfUrl = await this.$store.dispatch('getCgaDocument', {
          user: this.user.name,
          id: id,
        })
      } catch (e) {
        this.showModal = false
        this.apiError(e)
      }
    },
    closeModal () {
      this.showModal = false
    },
  },
}
</script>

<style lang="scss" scoped>
.router-link-active {
  font-weight: bold;
  background: #f5f5f5;
}

ul.menu-list {
  margin-bottom: 1em;
}

div.menu-head {
  display: flex;

  a.has-toggle {
    font-size: 0.8em;
  }

  a {
    background: none;
  }
}

.menu-head:hover {
  background-color: #f5f5f5;
}

router-link:hover {
  color: #ed9e1a;
}
</style>
